import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../hooks/useAppContext";
import Fade from "../Fade";
import { TransitionGroup } from "react-transition-group";
import { hasEmptyAmount } from "../../services/cart.service";
import {clearStorage} from "../../services/storage.service";

const MultiStepForm = (props) => {
  const {
    state,
    currentStep,
    children,
    actions,
    formRef,
    handleSubmit,
    onClick,
    getValues,
    isValid,
    customNextAction,
    sessionExpired,
    cssClasses,
  } = props;
  const { openMenu, items, paymentType, cart, isMobile, isLoading } =
    useAppContext();
  const [countItems, setCountItems] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [helpTip, setHelpTip] = useState(null);
  const [showHelpTip, setShowHelpTip] = useState(false);
  const donationOptions = state.checkoutDetails.donationOptions;
  const jointGift = state.checkoutDetails.jointGift;
  let next,
    prev = "",
    nextText = "Continue",
    prevText = "Previous";
  let navigate = useNavigate();

  useEffect(() => {
    const countItems = items.length;
    const countEmptyItems = items.filter(
      (v) => parseInt(v.attributes.total_price.number) === 0
    ).length;
    const yourCart = isMobile ? (
      <a href="#" tabIndex="0" onClick={openMenu}>
        your cart
      </a>
    ) : (
      <>your cart</>
    );
    const tip =
      countItems === 0 ? (
        <>To continue, add a gift to {yourCart}.</>
      ) : countEmptyItems === 1 && countItems === 1 ? (
        <>To continue, select a gift amount in {yourCart}.</>
      ) : countEmptyItems === 1 && countItems > 1 ? (
        <>To continue, select an amount or remove gift from {yourCart}.</>
      ) : countEmptyItems > 1 && countItems === countEmptyItems ? (
        <>To continue, select amounts and/or remove gifts from {yourCart}.</>
      ) : countEmptyItems > 1 && countItems !== countEmptyItems ? (
        <>To continue, select amounts or remove gifts from {yourCart}.</>
      ) : null;
    setHelpTip(tip);
    setCountItems(countItems);
    setDisabled(
      isLoading || countItems === 0 || (countItems > 0 && countEmptyItems > 0)
    );
    setShowHelpTip(showHelpTip && disabled && !isLoading && countItems > 0);
  }, [items, isMobile, isLoading]);

  // Instead of completely disabling the button, we let users click even when
  // the form is in an invalid state so we can intercept the button click
  // in order to stop propagation and show a help tip if there is something
  // preventing form submission.
  function handleOnClick(event) {
    if (countItems === 0 || hasEmptyAmount(items)) {
      event.preventDefault();
      event.stopPropagation();
      setShowHelpTip(true);
      return;
    }
    // The props.onClick is not always defined when we instantiate the
    // <MultiStepForm>, because sometimes all that is needed is the
    // props.handleSubmit. So we confirm that props.onClick is an
    // actual function before trying to call an `undefined` variable.
    if (typeof onClick === "function") {
      onClick(event);
    }
  }

  switch (currentStep) {
    case "CheckoutYourInfo":
      next = "checkout/personal-information";
      break;
    case "CheckoutPersonalInfo":
      prev = "checkout";
      next = donationOptions
        ? "checkout/honoree-information"
        : jointGift
        ? "checkout/spouse-information"
        : "checkout/payment";
      break;
    case "CheckoutHonoreeInfo":
      prev = "checkout/personal-information";
      next = jointGift ? "checkout/spouse-information" : "checkout/payment";
      break;
    case "CheckoutSpouseInfo":
      prev = donationOptions
        ? "checkout/honoree-information"
        : "checkout/personal-information";
      next = "checkout/payment";
      break;
    case "CheckoutPayment":
      prev = jointGift
        ? "checkout/spouse-information"
        : donationOptions
        ? "checkout/honoree-information"
        : "checkout/personal-information";
      next = "thank-you";
      nextText = "Pay Now";
  }

  const onSubmit = (checkoutDetails) => {
    actions.updateAction(checkoutDetails);

    if (currentStep === "CheckoutYourInfo") {
      const donationOptions = checkoutDetails.donationOptions;
      // Reset DonationOptions.
      if (typeof donationOptions === "undefined") {
        actions.updateAction({
          ...checkoutDetails,
          donationOptions: "",
        });
      }

      // Reset isPledgePayment.
      const isPledge = checkoutDetails.isPledgePayment;
      if (typeof isPledge === "undefined") {
        actions.updateAction({
          ...checkoutDetails,
          isPledgePayment: false,
          donationOptions: Array.isArray(checkoutDetails.donationOptions)
            ? checkoutDetails.donationOptions
            : checkoutDetails.donationOptions,
        });
      }

      // Reset JointGift.
      const jointGift = checkoutDetails.jointGift;
      if (typeof jointGift === "undefined") {
        actions.updateAction({
          ...checkoutDetails,
          jointGift: false,
          donationOptions: Array.isArray(checkoutDetails.donationOptions)
            ? checkoutDetails.donationOptions
            : checkoutDetails.donationOptions,
        });
      }
    }

    navigate(`/give/${next}`);
    if (next === "thank-you") {
      clearStorage();
      window.dataLayer = window.dataLayer || [];

      // Clear previous objects & items.
      window.dataLayer.push({
        ecommerce: null,
        event: null,
      });

      let products = [];
      items.map((item, index) => {
        products.push({
          id: item.relationships.purchased_entity.data.meta
            .drupal_internal__target_id,
          name: item.title,
          category: item.field_category_name,
          quantity: 1,
          price: item.attributes.unit_price.formatted.substring(1),
        });
      });

      window.dataLayer.push({
        event: "emory.ge.purchase",
        ecommerce: {
          currencyCode: "USD",
          purchase: {
            actionField: {
              id: items[0].relationships.order_id.data.meta
                .drupal_internal__target_id,
              affiliation: "Giving Experience",
              revenue: cart.attributes.total_price.formatted.substring(1),
            },
            products: products,
          },
        },
      });
    }
  };

  const goBack = () => {
    const values = getValues();
    actions.updateAction(values);
    navigate(`/give/${prev}`);
  };

  return (
    <form
      className={`checkout-form coh-style-container-theme---dark-1 ${currentStep} ${
        cssClasses ? cssClasses : ""
      }`}
      onSubmit={handleSubmit(onSubmit)}
      ref={formRef}
    >
      <fieldset>
        {children}
        <div className="checkout-form__actions">
          <fieldset
            disabled={sessionExpired}
            inert={sessionExpired ? "" : undefined}
            className="checkout-form__continue"
          >
            {customNextAction}
            <TransitionGroup component={null}>
              <Fade
                toggle={
                  currentStep !== "CheckoutPayment" ||
                  (currentStep === "CheckoutPayment" &&
                    paymentType === "creditCard")
                }
                timeout={200}
              >
                <button
                  className={`coh-style-link-button-with-icon--secondary${
                    !isValid ? " is-invalid" : ""
                  }${disabled ? " is-disabled" : ""}`}
                  onClick={handleOnClick}
                  disabled={sessionExpired}
                >
                  {nextText}
                </button>
              </Fade>
            </TransitionGroup>
          </fieldset>

          {prev ? (
            <button
              type="button"
              onClick={goBack}
              className="checkout-form__back coh-style-link-with-circle-icon-left"
            >
              {prevText}
            </button>
          ) : (
            <span data-flex-justify-placeholder="" />
          )}
        </div>
        {showHelpTip ? (
          <div className="checkout-form__actions-help">
            <div className="help-tip help-tip--top-right help-tip--sidebar-continue-button">
              {helpTip}
            </div>
          </div>
        ) : null}
      </fieldset>
    </form>
  );
};

export default MultiStepForm;
